<script setup>
import {computed, onUnmounted, ref} from "vue";
import axios from "axios";
import GameBoardView from "@/components/parts/GameBoardView.vue";
import {useAuth} from "@/composables/useAuth";

const props = defineProps({
  segments: {type: Array, required: true},
  sentence: {type: String, required: true},
  // Display-only variant of `sentence`; the raw sentence stays in the
  // /explore query, which is the only form the backend parser accepts.
  humanReadable: {type: String, default: null},
  caption: {type: String, required: true},
});

const COLS = 4;
const ROWS = 4;

const {isLoggedIn} = useAuth();

const liveSegments = ref(null);
const loading = ref(false);
const boardKey = ref(0);
const showLoginHint = ref(false);
let loginHintTimeout = null;

const displaySegments = computed(() => liveSegments.value ?? props.segments);

// Force text presentation (U+FE0E) on arrow glyphs — without it, iOS/WebKit
// sometimes falls back to Apple Color Emoji for these characters.
const displaySentence = computed(() => (props.humanReadable ?? props.sentence).replace(/[→↔]/g, '$&︎'));

function handleRefreshClick() {
  if (!isLoggedIn.value) {
    clearTimeout(loginHintTimeout);
    showLoginHint.value = true;
    loginHintTimeout = setTimeout(() => { showLoginHint.value = false; }, 4000);
    return;
  }
  regenerate();
}

onUnmounted(() => clearTimeout(loginHintTimeout));

async function regenerate() {
  loading.value = true;
  try {
    const url = `/explore?sentence=${encodeURIComponent(props.sentence)}&rowAmount=${ROWS}&colAmount=${COLS}&graph=true`;
    const response = await axios.get(url);
    if (response.data?.challengeUniverse?.segments) {
      liveSegments.value = response.data.challengeUniverse.segments;
      boardKey.value++;
    }
  } catch {
    // silently keep current board
  } finally {
    loading.value = false;
  }
}

const cellSize = computed(() => {
  if (typeof window === 'undefined') return 50;
  const w = window.innerWidth;
  if (w < 400) return Math.floor((w - 48) / COLS);
  return 60;
});

const gridStyles = computed(() => ({
  '--cellSizeInPx': cellSize.value + 'px',
  '--cellSize': String(cellSize.value),
  '--halfCellSizeInPx': (cellSize.value / 2) + 'px',
  '--halfCellSize': String(cellSize.value / 2),
  '--rowAmount': ROWS,
  '--colAmount': COLS,
}));
</script>

<template>
  <div class="tutorial-board">
    <div class="tutorial-board__header">
      <div>
        <p class="tutorial-board__sentence">
          <code class="predicate">{{ displaySentence }}</code>
        </p>
        <p class="tutorial-board__caption">{{ caption }}</p>
      </div>
      <button class="tutorial-board__refresh"
              :class="{ 'tutorial-board__refresh--loading': loading, 'tutorial-board__refresh--locked': !isLoggedIn }"
              :disabled="loading"
              @click="handleRefreshClick"
              :aria-label="isLoggedIn ? 'Generate new example' : 'Log in to generate new examples'"
              :title="isLoggedIn ? 'Generate new example' : 'Log in to generate new examples'">
        <i :class="isLoggedIn ? 'ri-loop-right-ai-line' : 'ri-lock-line'" aria-hidden="true"></i>
      </button>
      <Transition name="hint">
        <p v-if="showLoginHint" class="tutorial-board__login-hint">
          <router-link to="/account">Log in</router-link> to try new boards
        </p>
      </Transition>
    </div>
    <div class="tutorial-board__grid" :style="gridStyles">
      <GameBoardView
          :segments="displaySegments"
          :is-valid="true"
          :error-message="''"
          :game-board-class="'game-board'"
          :board-key="boardKey"
      />
    </div>
  </div>
</template>

<style scoped>
.tutorial-board {
  margin: 24px 0 32px;
}

.tutorial-board__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.tutorial-board__sentence {
  margin: 0 0 4px;
  font-size: 15px;
}

.tutorial-board__caption {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--text-color);
  line-height: 1.5;
}

.tutorial-board__refresh {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 5px;
  margin-top: 1px;
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-color);
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.15s, border-color 0.15s, color 0.15s;
}

.tutorial-board__refresh:hover:not(.tutorial-board__refresh--locked) {
  opacity: 1;
  border-color: var(--border-color-bright);
  color: var(--text-color-bright);
}

.tutorial-board__refresh:focus-visible {
  outline: 2px solid var(--text-color-bright);
  outline-offset: 2px;
  opacity: 1;
}

.tutorial-board__refresh:active {
  opacity: 0.7;
}

.tutorial-board__refresh--loading {
  opacity: 0.3;
  pointer-events: none;
}

.tutorial-board__refresh--locked {
  opacity: 0.35;
}

.tutorial-board__refresh--loading i {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.tutorial-board__refresh i {
  font-size: 16px;
  line-height: 1;
}

.tutorial-board__login-hint {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  left: auto;
  max-width: min(200px, 100%);
  z-index: 1;
  margin: 0;
  padding: 6px 10px;
  width: max-content;
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-color-bright);
  background: var(--background);
  border: 1px solid var(--border-color-bright);
  text-align: right;
}

.tutorial-board__login-hint a {
  color: var(--tomorrows-harvest);
}

.hint-enter-active {
  transition: opacity 0.2s var(--ease-out-quart), transform 0.2s var(--ease-out-quart);
}

.hint-leave-active {
  transition: opacity 0.15s ease-in;
}

.hint-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}

.hint-leave-to {
  opacity: 0;
}

.tutorial-board__grid {
  display: inline-block;
}

.predicate {
  font-family: var(--font), monospace;
  color: var(--tomorrows-harvest);
  background: none;
  padding: 0;
  font-size: inherit;
}

@media screen and (max-width: 639px) {
  .tutorial-board__grid {
    display: block;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tutorial-board__refresh--loading i {
    animation: none;
  }

  .hint-enter-active,
  .hint-leave-active {
    transition: opacity 0.01ms;
  }

  .hint-enter-from,
  .hint-leave-to {
    transform: none;
  }
}
</style>